import { useMemo } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import DashboardMealsLoading from '@/components/Parent/DashboardMealsLoading';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enUS } from 'date-fns/locale';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import parent from '@/api/parent';
import ChildCard from '@/components/Parent/ChildCard';
import ParentAppChrome from '@/components/Parent/ParentAppChrome';
import StickyReviewCartPay from '@/components/Parent/StickyReviewCartPay';
import WeekStrip from '@/components/Parent/WeekStrip';
import Navigation from '@/components/Navigation/Navigation';
import { route } from '@/constants/routeName';
import { EMPTY_CART_ITEMS, cartMealDates, cartOwnerIds, cartQuantityTotal, linesForChildAndDate, mealDateIso } from '@/helper/cartFamily';
import { dashboardDateHasPaid, isChildVisibleOnPlanner } from '@/helper/dashboardStatus';
import { mealWeekIsos } from '@/helper/mealWeek';
import { EMPTY_CHILDREN } from '@/helper/parentNormalize';
import { familyAccountHeading, schoolDisplayName } from '@/helper/schoolLabel';

const { getParentDashboard } = parent();

export default function Dashboard() {
	const dispatch = useDispatch();
	const user = useSelector((state) => state.auth.user);
	const accountType = useSelector((state) => state.auth.accountType);
	const children = useSelector((state) => state.auth.children) ?? EMPTY_CHILDREN;
	const childrenLoaded = useSelector((state) => state.auth.childrenLoaded);
	const key = useSelector((state) => state.auth.key);
	const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
	const parentOutlet = useSelector((state) => state.auth.parentOutlet);
	const orderDate = useSelector((state) => state.cart.orderDate);
	const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;
	const accountId = user?.id;

	const date = mealDateIso(orderDate || dayjs());

	const setMealDate = (iso) => {
		dispatch({ type: 'cart/setOrderDate', payload: iso });
	};

	const queryEnabled = Boolean(
		key && childrenLoaded && accountType === 'parent' && children.length > 0
	);

	const childrenKey = children.map((c) => c.id).join(',');

	const { data, isFetching, isError, error, refetch, isPlaceholderData } = useQuery({
		queryKey: ['parent-dashboard', accountId, date, childrenKey],
		enabled: queryEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const res = await getParentDashboard({
				date,
				token: key,
				children,
			});
			return res.data;
		},
	});

	const weekIsos = useMemo(() => mealWeekIsos(date), [date]);
	const sideIsos = useMemo(() => weekIsos.filter((iso) => iso !== date), [weekIsos, date]);
	const sideQueries = useMemo(
		() =>
			sideIsos.map((iso) => ({
				queryKey: ['parent-dashboard', accountId, iso, childrenKey],
				enabled: queryEnabled,
				staleTime: 5 * 60 * 1000,
				queryFn: () =>
					getParentDashboard({
						date: iso,
						token: key,
						children,
					}).then((res) => res.data),
			})),
		[sideIsos, accountId, childrenKey, queryEnabled, key, children]
	);
	const sideResults = useQueries({ queries: sideQueries });

	const cartDates = useMemo(() => cartMealDates(cartItems), [cartItems]);
	const dayMarks = useMemo(() => {
		const marks = {};
		weekIsos.forEach((iso) => {
			const fromSide = sideResults.some(
				(result, index) =>
					sideIsos[index] === iso && result.isSuccess && dashboardDateHasPaid(result.data)
			);
			const paid = fromSide || (iso === date && !isPlaceholderData && dashboardDateHasPaid(data));
			const cart = cartDates.has(iso);
			if (paid || cart) marks[iso] = { paid, cart };
		});
		return marks;
	}, [weekIsos, sideResults, sideIsos, date, isPlaceholderData, data, cartDates]);

	const summary = data?.summary;
	const cards = useMemo(() => {
		const list = data?.children || [];
		return list.filter(isChildVisibleOnPlanner);
	}, [data?.children]);

	const cartQty = cartQuantityTotal(cartItems);
	const ownersWithCart = cartOwnerIds(cartItems);
	const childrenWithCartOnDate = useMemo(() => {
		const ids = new Set();
		ownersWithCart.forEach((id) => {
			if (linesForChildAndDate(cartItems, id, date).length) ids.add(id);
		});
		return ids.size;
	}, [cartItems, date, ownersWithCart]);

	const progressLine = useMemo(() => {
		const paidKids = summary?.ordered ?? 0;
		const parts = [];
		if (paidKids > 0) {
			parts.push(`${paidKids} child${paidKids === 1 ? '' : 'ren'} with paid orders`);
		}
		if (childrenWithCartOnDate > 0) {
			parts.push(
				`${childrenWithCartOnDate} with selections in cart`
			);
		}
		if (!parts.length) {
			return 'No paid orders or cart selections for this date yet.';
		}
		return parts.join(' · ');
	}, [summary?.ordered, childrenWithCartOnDate]);

	const schoolLabel = schoolDisplayName({
		selectedOutlet,
		parentOutlet,
		dashboardOutlet: data?.outlet,
	});
	const showZeroChildren = childrenLoaded && children.length === 0 && accountType === 'parent';
	const showMealsLoading = queryEnabled && isFetching && !isError;
	const showError = isError;
	const showStickyPay = cartQty > 0;

	return (
		<section className='parent-ui'>
			<ParentAppChrome />
			<main>
				<p className='eyebrow'>{familyAccountHeading(schoolLabel)}</p>
				<h1>Meals for your children</h1>
				<p className='muted'>Choose meals across dates. Pay when you&apos;re ready.</p>

				<div className='datebar'>
					<div className='datebar-row'>
						<label htmlFor='dashboard-date'>Meal date</label>
						<div className='dateinput'>
							<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enUS}>
								<DatePicker
									value={date ? dayjs(date) : null}
									onChange={(value) => {
										if (value) setMealDate(dayjs(value).format('YYYY-MM-DD'));
									}}
									format='DD/MM/YYYY'
									slotProps={{
										textField: {
											id: 'dashboard-date',
											size: 'small',
											fullWidth: true,
											inputProps: { 'aria-label': 'Meal date' },
										},
									}}
								/>
							</LocalizationProvider>
						</div>
					</div>
					<WeekStrip dateIso={date} onSelect={setMealDate} dayMarks={dayMarks} />
				</div>

				{showZeroChildren && (
					<div className='flex flex-col gap-2 rounded-md border border-black bg-white p-4'>
						<h2 className='font-bold text-black'>No children linked</h2>
						<p className='text-sm text-[var(--weak)]'>
							Ask the school office to link a student to this parent account. You stay signed in.
						</p>
						<Link to={`/${route.account}`} className='text-sm font-bold text-black underline'>
							Go to account
						</Link>
					</div>
				)}

				{!showZeroChildren && showMealsLoading && <DashboardMealsLoading />}

				{!showZeroChildren && showError && (
					<div className='flex flex-col gap-3 rounded-md border border-black bg-white p-4' role='alert'>
						<p className='font-bold text-black'>Could not load dashboard</p>
						<p className='text-sm text-[var(--weak)]'>
							{error?.response?.data?.detail || error?.message || 'Something went wrong.'}
						</p>
						<button type='button' className='btn-actions h-11 rounded-md px-4 text-white' onClick={() => refetch()}>
							Retry
						</button>
					</div>
				)}

				{!showZeroChildren && data && !showError && !showMealsLoading && (
					<>
						<section className='flex flex-col gap-1' aria-labelledby='progress-heading'>
							<h2 id='progress-heading' className='text-base font-bold text-black'>
								Progress for this date
							</h2>
							<p className='text-sm text-[var(--weak)]' aria-live='polite'>
								{progressLine}
							</p>
						</section>
						<div className='kids'>
							{cards.map((child) => (
								<ChildCard key={child.id} child={child} mealDate={date} />
							))}
						</div>
						{cards.length === 0 && (
							<p className='muted'>No children to show for this date.</p>
						)}
					</>
				)}

				{showStickyPay && <StickyReviewCartPay />}
			</main>

			<Navigation />
		</section>
	);
}
